/**
 * Vyhľadávanie `/search?q=` (S11, F4AI-17) — `GET /search` nad FTS5
 * indexom. UI len prepíše dotaz z URL (header search box sem naviguje),
 * plné napojenie na hybridné vyhľadávanie je S12/S13.
 */
import { type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useApi } from '../api/ApiContext.js';
import { useResource } from '../hooks/useResource.js';
import { StateEmpty, StateError, StateLoading } from '../components/StateView.js';
import { formatRelativeTime, kindClassName, kindLabel } from '../lib/format.js';
import { npubShort } from '../lib/npub.js';

export function SearchPage(): JSX.Element {
  const [params] = useSearchParams();
  const query = params.get('q') ?? '';
  // F4AI-19: `?author=<64hex>` — gateway `/search` má `authors=` filter
  // (overené curlom), link z `/agents/:pubkey` ho predpĺňa; `q` je na
  // gateway povinný, takže bez dotazu sa len drží author-scope v URL.
  const rawAuthor = (params.get('author') ?? '').trim().toLowerCase();
  const author = /^[0-9a-f]{64}$/.test(rawAuthor) ? rawAuthor : '';
  const api = useApi();
  const results = useResource(
    () =>
      query === ''
        ? Promise.resolve(null)
        : api.search({ q: query, limit: 20, ...(author !== '' ? { authors: [author] } : {}) }),
    [api, query, author],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">search</h1>
          <p className="page-subtitle">
            {query === '' ? 'zadaj dotaz v poli vyššie' : `dotaz: “${query}” (FTS5 kanál, ${String(results.data?.took_ms ?? 0)} ms)`}
          </p>
        </div>
        {results.data !== null && <p className="untrusted-note">{String(results.data.count)} výsledkov</p>}
      </div>

      {query === '' && (
        <StateEmpty
          title="zatiaľ bez dotazu"
          hint={
            author !== ''
              ? 'tento pohľad filtruje len eventy jedného autora (authors= filter) — doplň dotaz, gateway vyžaduje q'
              : 'napr. /search?q=graceful+shutdown — dotaz preberá gateway od FTS5 indexa'
          }
        />
      )}
      {author !== '' && (
        <p className="claim-hint" data-testid="search-author-note">
          filter autora:{' '}
          <Link className="pubkey-chip" to={`/agents/${author}`} title={author}>
            {author.slice(0, 12)}…
          </Link>
        </p>
      )}
      {query !== '' && results.loading && <StateLoading label="vyhľadávam v indexe" />}
      {query !== '' && results.error !== null && <StateError error={results.error} onRetry={results.reload} />}
      {query !== '' && results.data !== null && results.data.results.length === 0 && (
        <StateEmpty title="nič nenájdené" hint="index neobsahuje event, ktorý by sedel na tento dotaz" />
      )}

      {query !== '' && results.data !== null && results.data.results.length > 0 && (
        <div className="stack" data-testid="search-results">
          {results.data.results.map((hit) => (
            <article key={hit.id} className="glass-card event-card" data-testid="search-result">
              <div className="event-meta">
                <span className={`kind-badge ${kindClassName(hit.kind)}`}>{kindLabel(hit.kind)}</span>
                <time dateTime={new Date(hit.created_at * 1000).toISOString()}>
                  {formatRelativeTime(hit.created_at)}
                </time>
                <Link className="pubkey-chip" to={`/agents/${hit.pubkey}`} title={hit.pubkey}>
                  {npubShort(hit.pubkey)}
                </Link>
                <span>score {hit.score.toFixed(2)}</span>
              </div>
              <h3>
                <Link to={`/threads/${hit.id}`}>{hit.addr ?? hit.id.slice(0, 16)}</Link>
              </h3>
              <p className="event-body">{hit.snippet}</p>
            </article>
          ))}
        </div>
      )}

      {results.data !== null && results.data.partial && (
        <p className="notice">výsledky sú čiastočné ({results.data.note ?? 'deadline'})</p>
      )}
      {results.data !== null && <p className="untrusted-note">{results.data.untrusted}</p>}
    </>
  );
}
